import { useLogin } from '@ixigo-packages/login';
import { ExpandMore, PersonFilled } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import Chip from '@ixigo/iui/Chip';
import ListItem from '@ixigo/iui/ListItem';
import ListItemIcon from '@ixigo/iui/ListItemIcon';
import ListItemText from '@ixigo/iui/ListItemText';
import Typography from '@ixigo/iui/Typography';
import { FC, createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { getWallet } from '../../apis/user';
import { formatPrice } from '../../utils/common';
import { getCookieHelpers } from '../../utils/cookie';
import { logout } from '../../utils/login';
import { getUserInfo } from '../../utils/user';
import { getUserMenuItems } from './helper';

const UserMenu: FC = () => {
    const { isUserLoggedIn, triggerLogin } = useLogin();
    const [userInfo, setuserInfo] = useState({ fname: 'ixigoer', lname: '', name: 'ixigoer', id: '' });
    const [balance, setBalance] = useState<number>(0);

    const nameInitials = useMemo(
        () => `${userInfo?.fname[0]?.toUpperCase() || ''}${userInfo?.lname[0]?.toUpperCase() || ''}`,
        [userInfo],
    );

    const userImageUrl = useMemo(
        () => (userInfo?.id ? `https://images.ixigo.com/node_image/user_pic/${userInfo?.id}.jpg` : ''),
        [userInfo],
    );

    const fetchUserInfo = useCallback(async () => {
        try {
            const response = await getUserInfo();
            if (!response) return;
            setuserInfo({
                fname: response.firstName || 'ixigoer',
                lname: response.lastName || '',
                name: response.name || 'ixigoer',
                id: response.userId || '',
            });
        } catch (e) {
            // NoOp
        }
    }, []);

    const fetchBalance = useCallback(async () => {
        try {
            const response = await getWallet();
            setBalance(response);
        } catch (e) {
            // NoOp
        }
    }, []);

    useEffect(() => {
        if (!isUserLoggedIn) return;
        fetchUserInfo();
        fetchBalance();
    }, [isUserLoggedIn, fetchUserInfo, fetchBalance]);

    const login = () => {
        triggerLogin(() => {
            getCookieHelpers().syncCookies();
            fetchUserInfo();
            fetchBalance();
            window.location.reload();
        });
    };

    if (!isUserLoggedIn)
        return (
            <div className='flex py-15 cursor-pointer'>
                <div className='flex justify-center items-center h-40 w-40 bg-blue-100 rounded-full'>
                    <PersonFilled className='text-blue-500' />
                </div>
                <Button variant='flat' onClick={login} size='md' className=' hover:bg-white text-neutral-800'>
                    Log in/Sign up
                </Button>
            </div>
        );

    return (
        <div className='group relative flex items-center gap-5 cursor-pointer'>
            <div className='flex items-center gap-10'>
                <div className='relative'>
                    {userImageUrl ? (
                        <img
                            src={userImageUrl}
                            alt='user-avatar'
                            className='h-40 w-40 border border-charcoal-100 rounded-full'
                            height={40}
                            width={40}
                        />
                    ) : (
                        <div className='h-40 w-40 rounded-full flex justify-center items-center bg-blue-100'>
                            <Typography variant='body-lg' className='text-blue-500'>
                                {nameInitials}
                            </Typography>
                        </div>
                    )}
                    {!!balance && (
                        <Chip color='danger' size='xs' variant='solid' className='absolute -top-[8px] left-20'>
                            {formatPrice(balance)}
                        </Chip>
                    )}
                </div>
                <Typography variant='body-md' className='text-neutral-800'>
                    <span className='flex items-center gap-5'>
                        Hey<span className='truncate max-w-[70px]'>{userInfo?.fname}</span>
                        <ExpandMore className='transition-transform group-hover:rotate-180' />
                    </span>
                </Typography>
            </div>
            <ul className='hidden absolute top-full right-0 w-[300px] py-10 bg-white rounded-20 overflow-hidden shadow-500 group-hover:block'>
                {getUserMenuItems(userInfo.name).map((item) => (
                    <a href={item.href} key={item.href} className='cursor-pointer'>
                        <ListItem>
                            <ListItemIcon type='avatar' className='items-center'>
                                {createElement(item.icon, { className: 'text-charcoal-600' })}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.title}
                                secondary={item.subtitle}
                                onClick={item?.key === 'logout' ? logout : () => {}}
                            />
                        </ListItem>
                    </a>
                ))}
            </ul>
        </div>
    );
};

export default UserMenu;
